import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import type { Course, Student, Enrollment } from '@/types/api'
import { adminKeys } from './useAdminQueries'

interface TermCourseWithStats {
  id: string
  termId: string
  courseId: string
  teacherId: string
  termName: string
  academicYear: string
  termStatus: string
  teacherFirstName: string
  teacherLastName: string
  enrollmentCount: number
  assignmentCount: number
  createdAt: string
}

interface CourseWithDepartment extends Course {
  departmentName?: string
}

interface CourseDetailResponse {
  course: CourseWithDepartment
  termCourses: TermCourseWithStats[]
}

// Query keys
export const courseDetailKeys = {
  all: ['courseDetail'] as const,
  detail: (id: string) => [...courseDetailKeys.all, id] as const,
  enrollments: (termCourseId: string) => [...courseDetailKeys.all, 'enrollments', termCourseId] as const,
  eligibleStudents: (termCourseId: string) => [...courseDetailKeys.all, 'eligible', termCourseId] as const,
}

// ============ Course Detail Query ============

export function useCourseDetail(courseId: string | undefined) {
  return useQuery({
    queryKey: courseDetailKeys.detail(courseId!),
    queryFn: async (): Promise<CourseDetailResponse> => {
      const res = await api.get<CourseDetailResponse>(`/api/courses/${courseId}`)
      return res.data
    },
    enabled: !!courseId,
  })
}

// ============ Eligible Students ============

// Students eligible to be newly added to a term-course: grade level intersects the
// course's gradeLevels (or the course has no restriction), and they aren't already
// actively enrolled. See apps/api/src/services/courseEligibility.ts for the single
// source of truth this endpoint is routed through.
export function useEligibleStudents(termCourseId: string | undefined) {
  return useQuery({
    queryKey: courseDetailKeys.eligibleStudents(termCourseId!),
    queryFn: async () => {
      const res = await api.get<{ students: Student[] }>(
        `/api/enrollments/eligible?termCourseId=${termCourseId}`
      )
      return res.data.students || []
    },
    enabled: !!termCourseId,
  })
}

// ============ Term Course Enrollments ============

export function useTermCourseEnrollments(termCourseId: string | undefined) {
  return useQuery({
    queryKey: courseDetailKeys.enrollments(termCourseId!),
    queryFn: async () => {
      const [studentsRes, enrollmentsRes, eligibleRes] = await Promise.all([
        api.get<{ students: Student[] }>('/api/students'),
        api.get<{ enrollments: Enrollment[] }>(`/api/enrollments/term-course/${termCourseId}`),
        api.get<{ students: Student[] }>(`/api/enrollments/eligible?termCourseId=${termCourseId}`),
      ])

      const enrollments = enrollmentsRes.data.enrollments || []
      // Students already actively enrolled must stay visible/toggleable (to remove them),
      // even if the eligible-students endpoint excludes them for being already-enrolled
      // or if a later grade change made them no longer grade-eligible. "Only eligible
      // students should appear" governs who can be newly *added*, not who can be seen.
      const activeStudentIds = new Set(
        enrollments.filter((e) => e.status === 'active').map((e) => e.studentId)
      )
      const eligibleStudentIds = new Set(eligibleRes.data.students.map((s) => s.id))

      const students = (studentsRes.data.students || []).filter(
        (s) => activeStudentIds.has(s.id) || eligibleStudentIds.has(s.id)
      )

      return { students, enrollments }
    },
    enabled: !!termCourseId,
  })
}

// ============ Save Enrollments Mutation ============

export function useSaveCourseEnrollments() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      termCourseId,
      currentEnrollments,
      selectedStudentIds,
    }: {
      termCourseId: string
      currentEnrollments: Enrollment[]
      selectedStudentIds: Set<string>
    }) => {
      // Only consider active enrollments as "current" - withdrawn/dropped students can be re-enrolled
      const activeEnrollments = currentEnrollments.filter((e) => e.status === 'active')
      const currentStudentIds = new Set(activeEnrollments.map((e) => e.studentId))

      // Students to enroll (in selected but not currently enrolled)
      const toEnroll = Array.from(selectedStudentIds).filter((id) => !currentStudentIds.has(id))

      // Students to unenroll (withdraw) - currently active but not in selected
      const toUnenroll = activeEnrollments.filter((e) => !selectedStudentIds.has(e.studentId))

      // Create new enrollments
      await Promise.all(
        toEnroll.map((studentId) =>
          api.post('/api/enrollments', {
            studentId,
            termCourseId,
            status: 'active',
            enrollmentDate: new Date().toISOString().split('T')[0],
          })
        )
      )

      // Withdraw removed enrollments (soft delete - preserves grades)
      await Promise.all(toUnenroll.map((e) => api.delete(`/api/enrollments/${e.id}`)))

      return { enrolled: toEnroll.length, unenrolled: toUnenroll.length }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: courseDetailKeys.detail(variables.termCourseId.toString()) })
      queryClient.invalidateQueries({ queryKey: courseDetailKeys.enrollments(variables.termCourseId) })
      queryClient.invalidateQueries({ queryKey: adminKeys.enrollments() })
    },
  })
}

// ============ Calendar / Upcoming Assignments ============

interface UpcomingAssignment {
  id: string
  termCourseId: string
  assignmentName: string
  description: string | null
  totalPoints: number
  dueDate: string
  assignedDate: string
  termName: string
  courseName: string
  courseCode: string
}

export const calendarKeys = {
  all: ['calendar'] as const,
  upcoming: (params: { days?: number; studentId?: string; termCourseId?: string }) =>
    [...calendarKeys.all, 'upcoming', params] as const,
}

export function useUpcomingAssignments(params: {
  days?: number
  studentId?: string
  termCourseId?: string
  limit?: number
}) {
  return useQuery({
    queryKey: calendarKeys.upcoming({
      days: params.days,
      studentId: params.studentId,
      termCourseId: params.termCourseId,
    }),
    queryFn: async () => {
      let url = '/api/calendar/upcoming'
      const searchParams = new URLSearchParams()

      if (params.studentId) {
        url = `/api/calendar/student/${params.studentId}/upcoming`
      }

      if (params.days) {
        searchParams.append('days', params.days.toString())
      }

      if (params.termCourseId) {
        searchParams.append('termCourseId', params.termCourseId.toString())
      }

      const queryString = searchParams.toString()
      const fullUrl = queryString ? `${url}?${queryString}` : url

      const res = await api.get<{ assignments: UpcomingAssignment[] }>(fullUrl)
      let assignments = res.data.assignments

      // Apply limit if specified
      if (params.limit) {
        assignments = assignments.slice(0, params.limit)
      }

      return assignments
    },
  })
}

// ============ Impersonation Users ============

interface ImpersonatableUser {
  id: string
  firstName: string
  lastName: string
  email: string | null
}

export const impersonationKeys = {
  all: ['impersonation'] as const,
  users: (role: string, search: string) => [...impersonationKeys.all, 'users', role, search] as const,
}

export function useImpersonatableUsers(role: string, search: string, enabled: boolean) {
  return useQuery({
    queryKey: impersonationKeys.users(role, search),
    queryFn: async () => {
      const params = new URLSearchParams({ role })
      if (search.trim()) {
        params.append('search', search.trim())
      }
      const res = await api.get<{ users: ImpersonatableUser[] }>(
        `/api/auth/impersonatable-users?${params}`
      )
      return res.data.users
    },
    enabled,
  })
}

// ============ Settings - Categories ============

export const settingsKeys = {
  all: ['settings'] as const,
  categories: (termCourseId: string) => [...settingsKeys.all, 'categories', termCourseId] as const,
  gradeScales: (programType: string) => [...settingsKeys.all, 'gradeScales', programType] as const,
}

interface AssignmentCategory {
  id: string
  termCourseId: string
  name: string
  weight: number
}

export interface CategoriesResponse {
  categories: AssignmentCategory[]
  /** Fraction, 0-1. */
  totalWeight: number
  /**
   * The server's verdict on whether the set totals 100% (AR-11). The UI keys its
   * meters off this rather than recomputing a tolerance of its own - one
   * definition of "complete", and it lives on the server.
   */
  isComplete: boolean
}

export function useCategories(termCourseId: string | null) {
  return useQuery({
    queryKey: settingsKeys.categories(termCourseId!),
    queryFn: async (): Promise<CategoriesResponse> => {
      const res = await api.get<CategoriesResponse>(
        `/api/categories/term-course/${termCourseId}`
      )
      return res.data
    },
    enabled: !!termCourseId,
  })
}

export function useCreateCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: { termCourseId: string; name: string; weight: number }) => {
      const res = await api.post<{ category: AssignmentCategory }>('/api/categories', data)
      return res.data.category
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.categories(variables.termCourseId) })
    },
  })
}

export function useUpdateCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      termCourseId: string
      data: { name: string; weight: number }
    }) => {
      const res = await api.put<{ category: AssignmentCategory }>(`/api/categories/${id}`, data)
      return res.data.category
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.categories(variables.termCourseId) })
    },
  })
}

/**
 * Whole-set re-weight of a course's categories (AR-11).
 *
 * `PUT /api/categories/term-course/:id/weights` is the only write that can move
 * weight *between* areas: a set that already totals 100% cannot be re-weighted
 * one category at a time without passing through a total the server refuses.
 * `weights` is the whole prospective set; categories left out keep their current
 * weight and still count toward the total.
 *
 * A removal rides along in the same submission. A bare DELETE out of a complete
 * set is refused too, so the area is zeroed by the rebalance first and deleted
 * afterwards - at which point the remaining areas already account for 100%. If
 * the rebalance is refused, nothing is deleted.
 *
 * So does an addition. `POST /api/categories` refuses any weight above 0 once
 * the set totals 100%, so a new category is created at 0% - which keeps the set
 * at 100% and gradeable throughout - and given its weight by the rebalance that
 * follows. If that rebalance is refused, the category is left at 0%: a valid
 * state the admin can finish or remove, not a broken one.
 */
export function useRebalanceCategoryWeights() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      termCourseId,
      weights,
      removeIds = [],
      create,
    }: {
      termCourseId: string
      weights: Array<{ id: string; weight: number }>
      removeIds?: string[]
      create?: { name: string; weight: number }
    }): Promise<CategoriesResponse> => {
      const allWeights = [...weights]

      if (create) {
        const created = await api.post<{ category: { id: string } }>('/api/categories', {
          termCourseId,
          name: create.name,
          weight: 0,
        })
        allWeights.push({ id: created.data.category.id, weight: create.weight })
      }

      const res = await api.put<CategoriesResponse>(
        `/api/categories/term-course/${termCourseId}/weights`,
        { weights: allWeights }
      )

      // Sequential, not concurrent: each delete is validated against the set as
      // it stands at that moment.
      for (const id of removeIds) {
        await api.delete(`/api/categories/${id}`)
      }

      return res.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.categories(variables.termCourseId) })
    },
  })
}

export function useDeleteCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id }: { id: string; termCourseId: string }) => {
      await api.delete(`/api/categories/${id}`)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.categories(variables.termCourseId) })
    },
  })
}

// ============ Term Courses (for dropdowns) ============

interface TermCourse {
  id: string
  termId: string
  courseId: string
  teacherId: string
  termName: string
  academicYear: string
  courseCode: string
  courseName: string
}

export const termCourseKeys = {
  all: ['termCourses'] as const,
  list: () => [...termCourseKeys.all, 'list'] as const,
}

export function useTermCourses() {
  return useQuery({
    queryKey: termCourseKeys.list(),
    queryFn: async () => {
      const res = await api.get<{ termCourses: TermCourse[] }>('/api/term-courses')
      return res.data.termCourses || []
    },
  })
}

// ============ Grade Scales ============

interface GradeScale {
  id: string
  programType: 'IHP' | 'DP' | 'Both' | 'Standard'
  minPercentage: number
  maxPercentage: number
  ibGrade: number | null
  letterGrade: string
  gpaPoints: number
  transcriptPercentage: number
  isDefault: boolean
  createdAt: string
  updatedAt: string
}

export const gradeScaleKeys = {
  all: ['gradeScales'] as const,
  list: (programType: string) => [...gradeScaleKeys.all, 'list', programType] as const,
}

export function useGradeScales(programType: string) {
  return useQuery({
    queryKey: gradeScaleKeys.list(programType),
    queryFn: async () => {
      const res = await api.get<{ gradeScales: GradeScale[] }>(
        `/api/grade-scales?programType=${programType}`
      )
      return res.data.gradeScales.sort((a, b) => b.minPercentage - a.minPercentage)
    },
  })
}

export function useCreateGradeScale() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (
      data: Omit<GradeScale, 'id' | 'createdAt' | 'updatedAt'>
    ) => {
      const res = await api.post<{ gradeScale: GradeScale }>('/api/grade-scales', data)
      return res.data.gradeScale
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: gradeScaleKeys.list(variables.programType) })
    },
  })
}

export function useUpdateGradeScale() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      programType: string
      data: Partial<Omit<GradeScale, 'id' | 'createdAt' | 'updatedAt'>>
    }) => {
      const res = await api.put<{ gradeScale: GradeScale }>(`/api/grade-scales/${id}`, data)
      return res.data.gradeScale
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: gradeScaleKeys.list(variables.programType) })
    },
  })
}

export function useDeleteGradeScale() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id }: { id: string; programType: string }) => {
      await api.delete(`/api/grade-scales/${id}`)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: gradeScaleKeys.list(variables.programType) })
    },
  })
}
